import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, Form, Spinner } from 'react-bootstrap'
import Message from '../components/Message'
import CourseAutocomplete from '../components/CourseAutocomplete'
import { getUserDetails, updateUser, getUserRoles, updateUserRoles, adminUploadUserImage } from '../actions/userActions'
import { USER_UPDATE_RESET, USER_ROLES_UPDATE_RESET, USER_PROFILE_IMAGE_RESET } from '../constants/userConstants'

// ── Profile model choices (mirrors Django) ────────────────────────────────────
const SHIRT_SIZES    = ['M','L','XL','XXL','LT']
const PANT_WAISTS    = [26,28,30,32,34,36,38,40,42,44]
const PANT_INSEAMS   = [28,29,30,31,32,33,34,35,36]
const SHOE_SIZES     = ['6','6.5','7','7.5','8','8.5','9','9.5','10','10.5','11','11.5','12','12.5','13','14','15']
const SHOE_WIDTHS    = ['D','EE','Wide','Narrow']
const GLOVE_SIZES    = ['M','L','XL','Cadet M','S']
const DOMINANT_HANDS = ['Right','Left']
const ALL_ROLES      = ['journalist','general']

// ── Small helpers ─────────────────────────────────────────────────────────────
const SectionHead = ({ icon, label }) => (
    <div style={{
        display: 'flex', alignItems: 'center', gap: '0.6rem',
        borderBottom: '2px solid var(--bnb-navy)',
        paddingBottom: '0.4rem', marginBottom: '1.25rem', marginTop: '2rem',
    }}>
        <i className={`fa-solid ${icon}`} style={{ color: 'var(--bnb-gold)', fontSize: '0.95rem' }} />
        <span style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--bnb-navy)' }}>
            {label}
        </span>
    </div>
)

const FieldGroup = ({ label, children }) => (
    <Form.Group className="mb-3">
        <Form.Label style={{ fontSize: '0.8rem', fontWeight: 700, color: '#374151', marginBottom: '0.3rem' }}>
            {label}
        </Form.Label>
        {children}
    </Form.Group>
)

const inputStyle = {
    fontSize: '0.875rem',
    border: '1.5px solid var(--bnb-border, #e5e7eb)',
    borderRadius: 8,
    padding: '0.5rem 0.75rem',
}

function UserEditScreen() {
    const { id } = useParams()
    const dispatch = useDispatch()
    const imageInputRef = useRef()

    // ── Auth fields
    const [name, setName]         = useState('')
    const [email, setEmail]       = useState('')
    const [isAdmin, setIsAdmin]   = useState(false)
    const [password, setPassword] = useState('')

    // ── Profile image
    const [imageFile, setImageFile]       = useState(null)
    const [imagePreview, setImagePreview] = useState('')

    // ── Golf
    const [handicap, setHandicap]         = useState('')
    const [dominantHand, setDominantHand] = useState('')
    const [homeCourse, setHomeCourse]     = useState(null) // { id, name }

    // ── Personal
    const [birthDate, setBirthDate]   = useState('')
    const [birthPlace, setBirthPlace] = useState('')
    const [country, setCountry]       = useState('')
    const [college, setCollege]       = useState('')

    // ── Sizing
    const [shirtSize, setShirtSize]   = useState('')
    const [pantWaist, setPantWaist]   = useState('')
    const [pantInseam, setPantInseam] = useState('')
    const [shoeSize, setShoeSize]     = useState('')
    const [shoeWidth, setShoeWidth]   = useState('')
    const [gloveSize, setGloveSize]   = useState('')

    // ── Roles
    const [selectedRoles, setSelectedRoles] = useState([])

    // ── Feedback messages
    const [saveMsg, setSaveMsg] = useState('')
    const [saveErr, setSaveErr] = useState('')
    const [imgMsg, setImgMsg]   = useState('')

    // ── Redux state
    const userDetails = useSelector(s => s.userDetails)
    const { loading, error, user } = userDetails

    const userLogin = useSelector(s => s.userLogin)
    const { userInfo } = userLogin

    const userUpdate = useSelector(s => s.userUpdate)
    const { loading: updating, error: updateError, success } = userUpdate

    const userRoles = useSelector(s => s.userRoles)
    const { roles: fetchedRoles = [] } = userRoles

    const userRolesUpdate = useSelector(s => s.userRolesUpdate)
    const { loading: rolesUpdating, error: rolesError, success: rolesSuccess } = userRolesUpdate

    const profileImageUpload = useSelector(s => s.profileImageUpload)
    const { loading: imgUploading, success: imgSuccess, error: imgError } = profileImageUpload

    // ── Load user on mount
    useEffect(() => {
        if (!user.name || user._id !== Number(id)) {
            dispatch(getUserDetails(id))
            dispatch(getUserRoles(id))
        }
    }, [id, dispatch]) // eslint-disable-line

    // ── Populate form when user data arrives
    useEffect(() => {
        if (user && user._id === Number(id)) {
            setName(user.name || '')
            setEmail(user.email || '')
            setIsAdmin(user.isAdmin || false)
            setHandicap(user.handicap != null ? String(user.handicap) : '')
            setDominantHand(user.dominant_hand || '')
            setHomeCourse(user.home_course_id ? { id: user.home_course_id, name: user.home_course_name } : null)
            setBirthDate(user.birth_date || '')
            setBirthPlace(user.birth_place || '')
            setCountry(user.country || '')
            setCollege(user.college || '')
            setShirtSize(user.shirt_size || '')
            setPantWaist(user.pant_waist ? String(user.pant_waist) : '')
            setPantInseam(user.pant_inseam ? String(user.pant_inseam) : '')
            setShoeSize(user.shoe_size_us || '')
            setShoeWidth(user.shoe_width || '')
            setGloveSize(user.glove_size || '')
        }
    }, [user, id])

    useEffect(() => { setSelectedRoles(fetchedRoles) }, [fetchedRoles])

    useEffect(() => {
        if (success) {
            dispatch({ type: USER_UPDATE_RESET })
            setSaveMsg('Profile saved.')
            setSaveErr('')
        }
        if (updateError) setSaveErr(updateError)
    }, [success, updateError, dispatch])

    useEffect(() => {
        if (rolesSuccess) dispatch({ type: USER_ROLES_UPDATE_RESET })
    }, [rolesSuccess, dispatch])

    useEffect(() => {
        if (imgSuccess) {
            dispatch({ type: USER_PROFILE_IMAGE_RESET })
            setImageFile(null)
            setImagePreview('')
            setImgMsg('Image updated.')
        }
    }, [imgSuccess, dispatch])

    // ── Handlers
    const handleImageChange = (e) => {
        const file = e.target.files[0]
        if (!file) return
        setImageFile(file)
        setImagePreview(URL.createObjectURL(file))
        setImgMsg('')
    }

    const toggleRole = (role) => {
        setSelectedRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role])
    }

    const submitHandler = (e) => {
        e.preventDefault()
        setSaveMsg('')
        setSaveErr('')
        dispatch(updateUser({
            _id: user._id,
            name, email, isAdmin,
            ...(password ? { password } : {}),
            handicap:      handicap || null,
            dominant_hand: dominantHand,
            birth_date:    birthDate || null,
            birth_place:   birthPlace,
            country,
            college,
            shirt_size:    shirtSize,
            pant_waist:    pantWaist || null,
            pant_inseam:   pantInseam || null,
            shoe_size_us:  shoeSize,
            shoe_width:    shoeWidth,
            glove_size:    gloveSize,
            home_course_id: homeCourse?.id || null,
        }))
    }

    const currentImage = imagePreview || (user && user.image) || null

    return (
        <div style={{ paddingTop: '0.5rem' }}>

            <Link to='/admin/userlist' style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                fontSize: '0.8rem', fontWeight: 700, color: 'var(--bnb-navy)',
                textDecoration: 'none', marginBottom: '1.25rem',
            }}>
                <i className="fa-solid fa-arrow-left" /> Back to Users
            </Link>

            {/* ── Header ─────────────────────────────────────────────────── */}
            <div style={{
                background: 'linear-gradient(135deg, var(--bnb-navy) 0%, #001f5c 60%, #0a0a2e 100%)',
                borderRadius: '10px 10px 0 0',
                padding: '1.5rem 2rem',
                color: '#fff',
                display: 'flex', alignItems: 'center', gap: '1rem',
            }}>
                <i className="fa-solid fa-user-gear" style={{ fontSize: '1.5rem', color: 'var(--bnb-gold)' }} />
                <div>
                    <div style={{ fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                        Admin · Edit User
                    </div>
                    <div style={{ fontSize: '1.3rem', fontWeight: 900, lineHeight: 1.2 }}>
                        {user.name || `User #${id}`}
                    </div>
                </div>
            </div>

            {/* ── Body ───────────────────────────────────────────────────── */}
            <div className="bnb-card" style={{ borderRadius: '0 0 10px 10px', padding: '2rem' }}>

                {loading && <div style={{ textAlign: 'center', padding: '2rem' }}><Spinner animation="border" style={{ color: 'var(--bnb-navy)' }} /></div>}
                {error    && <Message variant='danger'>{error}</Message>}
                {saveMsg  && <Message variant='success'>{saveMsg}</Message>}
                {saveErr  && <Message variant='danger'>{saveErr}</Message>}

                <Form onSubmit={submitHandler}>

                    {/* ── Profile Photo ─────────────────────────────────── */}
                    <SectionHead icon="fa-image" label="Profile Photo" />
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1.5rem', marginBottom: '1rem' }}>
                        {currentImage ? (
                            <img src={currentImage} alt="profile" style={{
                                width: 90, height: 90, borderRadius: '50%', objectFit: 'cover',
                                border: '3px solid var(--bnb-gold)', boxShadow: '0 2px 12px rgba(0,0,0,0.15)', flexShrink: 0,
                            }} />
                        ) : (
                            <div style={{
                                width: 90, height: 90, borderRadius: '50%', background: 'var(--bnb-navy)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '2rem', color: 'rgba(255,255,255,0.4)',
                                border: '3px solid #e5e7eb', flexShrink: 0,
                            }}>
                                <i className="fa-solid fa-user" />
                            </div>
                        )}
                        <div style={{ flex: 1 }}>
                            <input type="file" accept="image/*" ref={imageInputRef} style={{ display: 'none' }} onChange={handleImageChange} />
                            <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                                <button type="button" onClick={() => imageInputRef.current.click()}
                                    style={{ background: 'var(--bnb-navy)', color: '#fff', border: 'none', borderRadius: 8, padding: '0.45rem 1rem', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}>
                                    <i className="fa-solid fa-folder-open me-2" />Choose Photo
                                </button>
                                {imageFile && (
                                    <button type="button" onClick={() => dispatch(adminUploadUserImage(id, imageFile))} disabled={imgUploading}
                                        style={{ background: 'var(--bnb-gold)', color: '#3d2a00', border: 'none', borderRadius: 8, padding: '0.45rem 1rem', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}>
                                        {imgUploading ? <Spinner animation="border" size="sm" /> : <><i className="fa-solid fa-upload me-2" />Upload</>}
                                    </button>
                                )}
                            </div>
                            {imageFile && <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '0.4rem' }}>{imageFile.name}</div>}
                            {imgMsg   && <div style={{ fontSize: '0.78rem', color: '#15803d', marginTop: '0.4rem', fontWeight: 600 }}>{imgMsg}</div>}
                            {imgError && <div style={{ fontSize: '0.78rem', color: '#dc2626', marginTop: '0.4rem' }}>{imgError}</div>}
                        </div>
                    </div>

                    {/* ── Account ───────────────────────────────────────── */}
                    <SectionHead icon="fa-user-shield" label="Account" />
                    <Row>
                        <Col md={6}>
                            <FieldGroup label="Full Name">
                                <Form.Control style={inputStyle} type="text" value={name} onChange={e => setName(e.target.value)} />
                            </FieldGroup>
                        </Col>
                        <Col md={6}>
                            <FieldGroup label="Email / Username">
                                <Form.Control style={inputStyle} type="email" value={email} onChange={e => setEmail(e.target.value)} />
                            </FieldGroup>
                        </Col>
                        <Col md={6}>
                            <FieldGroup label="New Password (leave blank to keep current)">
                                <Form.Control style={inputStyle} type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} />
                            </FieldGroup>
                        </Col>
                        <Col md={6} style={{ display: 'flex', alignItems: 'center', paddingTop: '1.75rem' }}>
                            <Form.Check type="switch" id="isAdmin"
                                label={<span style={{ fontWeight: 700, fontSize: '0.875rem' }}>Admin Access</span>}
                                checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} />
                        </Col>
                    </Row>

                    {/* ── Golf Profile ──────────────────────────────────── */}
                    <SectionHead icon="fa-flag" label="Golf Profile" />
                    <Row>
                        <Col md={4}>
                            <FieldGroup label="Handicap">
                                <Form.Control style={inputStyle} type="number" step="0.1" placeholder="e.g. 14.2" value={handicap} onChange={e => setHandicap(e.target.value)} />
                            </FieldGroup>
                        </Col>
                        <Col md={4}>
                            <FieldGroup label="Dominant Hand">
                                <Form.Select style={inputStyle} value={dominantHand} onChange={e => setDominantHand(e.target.value)}>
                                    <option value="">-- Select --</option>
                                    {DOMINANT_HANDS.map(h => <option key={h} value={h}>{h}</option>)}
                                </Form.Select>
                            </FieldGroup>
                        </Col>
                        <Col md={4}>
                            <FieldGroup label="Home Course">
                                <CourseAutocomplete
                                    value={homeCourse}
                                    onChange={setHomeCourse}
                                    userInfo={userInfo}
                                />
                            </FieldGroup>
                        </Col>
                    </Row>

                    {/* ── Personal ──────────────────────────────────────── */}
                    <SectionHead icon="fa-earth-americas" label="Personal" />
                    <Row>
                        <Col md={4}>
                            <FieldGroup label="Date of Birth">
                                <Form.Control style={inputStyle} type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} />
                            </FieldGroup>
                        </Col>
                        <Col md={4}>
                            <FieldGroup label="Birth City / Place">
                                <Form.Control style={inputStyle} type="text" placeholder="e.g. Atlanta, GA" value={birthPlace} onChange={e => setBirthPlace(e.target.value)} />
                            </FieldGroup>
                        </Col>
                        <Col md={4}>
                            <FieldGroup label="Country">
                                <Form.Control style={inputStyle} type="text" placeholder="e.g. US or United States" value={country} onChange={e => setCountry(e.target.value)} />
                            </FieldGroup>
                        </Col>
                        <Col md={6}>
                            <FieldGroup label="College / University">
                                <Form.Control style={inputStyle} type="text" value={college} onChange={e => setCollege(e.target.value)} />
                            </FieldGroup>
                        </Col>
                    </Row>

                    {/* ── Sizing ────────────────────────────────────────── */}
                    <SectionHead icon="fa-ruler" label="Sizing" />
                    <Row>
                        <Col md={3}>
                            <FieldGroup label="Shirt Size">
                                <Form.Select style={inputStyle} value={shirtSize} onChange={e => setShirtSize(e.target.value)}>
                                    <option value="">--</option>
                                    {SHIRT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                                </Form.Select>
                            </FieldGroup>
                        </Col>
                        <Col md={3}>
                            <FieldGroup label="Pant Waist">
                                <Form.Select style={inputStyle} value={pantWaist} onChange={e => setPantWaist(e.target.value)}>
                                    <option value="">--</option>
                                    {PANT_WAISTS.map(s => <option key={s} value={s}>{s}</option>)}
                                </Form.Select>
                            </FieldGroup>
                        </Col>
                        <Col md={3}>
                            <FieldGroup label="Pant Inseam">
                                <Form.Select style={inputStyle} value={pantInseam} onChange={e => setPantInseam(e.target.value)}>
                                    <option value="">--</option>
                                    {PANT_INSEAMS.map(s => <option key={s} value={s}>{s}</option>)}
                                </Form.Select>
                            </FieldGroup>
                        </Col>
                        <Col md={3}>
                            <FieldGroup label="Glove Size">
                                <Form.Select style={inputStyle} value={gloveSize} onChange={e => setGloveSize(e.target.value)}>
                                    <option value="">--</option>
                                    {GLOVE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                                </Form.Select>
                            </FieldGroup>
                        </Col>
                        <Col md={3}>
                            <FieldGroup label="Shoe Size (US)">
                                <Form.Select style={inputStyle} value={shoeSize} onChange={e => setShoeSize(e.target.value)}>
                                    <option value="">--</option>
                                    {SHOE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                                </Form.Select>
                            </FieldGroup>
                        </Col>
                        <Col md={3}>
                            <FieldGroup label="Shoe Width">
                                <Form.Select style={inputStyle} value={shoeWidth} onChange={e => setShoeWidth(e.target.value)}>
                                    <option value="">--</option>
                                    {SHOE_WIDTHS.map(s => <option key={s} value={s}>{s}</option>)}
                                </Form.Select>
                            </FieldGroup>
                        </Col>
                    </Row>

                    {/* ── Save ──────────────────────────────────────────── */}
                    <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                        <button type="submit" disabled={updating} style={{
                            background: 'linear-gradient(135deg, var(--bnb-navy), #001f5c)',
                            color: '#fff', border: 'none', borderRadius: 10,
                            padding: '0.65rem 2rem', fontSize: '0.875rem', fontWeight: 800,
                            letterSpacing: '0.04em', cursor: 'pointer',
                            boxShadow: '0 2px 10px rgba(0,48,135,0.25)',
                        }}>
                            {updating
                                ? <><Spinner animation="border" size="sm" className="me-2" />Saving…</>
                                : <><i className="fa-solid fa-floppy-disk me-2" />Save Profile</>}
                        </button>
                    </div>
                </Form>

                {/* ── Roles ─────────────────────────────────────────────── */}
                <SectionHead icon="fa-tags" label="Roles" />
                {rolesError   && <Message variant='danger'>{rolesError}</Message>}
                {rolesSuccess && <Message variant='success'>Roles updated.</Message>}
                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    {ALL_ROLES.map(role => (
                        <Form.Check key={role} type="checkbox" id={`role-${role}`}
                            label={role.charAt(0).toUpperCase() + role.slice(1)}
                            checked={selectedRoles.includes(role)}
                            onChange={() => toggleRole(role)}
                            style={{ fontWeight: 600, fontSize: '0.875rem' }} />
                    ))}
                </div>
                <button type="button" onClick={() => dispatch(updateUserRoles(id, selectedRoles))} disabled={rolesUpdating}
                    style={{ background: 'var(--bnb-gold)', color: '#3d2a00', border: 'none', borderRadius: 8, padding: '0.45rem 1.25rem', fontSize: '0.8rem', fontWeight: 800, cursor: 'pointer' }}>
                    {rolesUpdating ? <Spinner animation="border" size="sm" /> : <><i className="fa-solid fa-check me-1" />Save Roles</>}
                </button>
            </div>
        </div>
    )
}

export default UserEditScreen

